import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ResponseOfferType,
  SuccessOfferCreateType,
  VaultProfileDataAction,
  VaultProfileType,
  VaultTransactionHistoryType,
  VaultUserInfo,
} from 'store/slices/vault/types';
import { userLogoutRequest } from 'store/slices/user/actionAsync';
import { VaultAllowedTransactionType } from 'widgets/LW/types';
import { VaultWalletResponseType } from 'widgets/WalletWidget/components/Vault/VaultWalletCard';
import {
  getKYC,
  getVaultProfileData,
  getVaultTransactionHistory,
  getVaultWallets,
  VaultKYCResponse,
  vaultConnectWalletRequest,
  vaultRegistrationRequest,
  createOffer,
  VaultResponse,
} from './actionAsync';

const AllowedTransactions = [
  { model: 'receiveCryptoModel', title: 'Crypto Received' },
  { model: 'payinAdvcashModel', title: 'AdvCash Top-Up' },
  { model: 'payinCardModel', title: 'Card Top-Up' },
  { model: 'payoutCardModel', title: 'Withdraw to Card' },
  { model: 'sendToWalletModel', title: 'Transfer To Wallet' },
  { model: 'sendToPhoneModel', title: 'Transfer by Phone' },
];

type InitState = {
  phone: string;
  isLoading: boolean;
  isKYCLoading: boolean;
  wallets: [];
  transactionHistory: [];
  vaultProfile: VaultProfileType | null;
  kyc: VaultKYCResponse | null;
  vaultUserInfo: VaultUserInfo | null;
  isConfirmUserEmail: boolean;
  offer: ResponseOfferType;
  createdOffer: SuccessOfferCreateType;
  isOfferLoading: boolean;
  isConfirmOfferLoading: boolean;
  isPayInStatusLoading: boolean;
  isCardsDataLoading: boolean;
};

const initState: InitState = {
  phone: '',
  isLoading: false,
  isKYCLoading: false,
  wallets: [],
  transactionHistory: [],
  vaultProfile: null,
  kyc: null,
  vaultUserInfo: null,
  isConfirmUserEmail: false,
  offer: {
    offerId: 0,
    rate: {
      rate: 0,
    },
    feeInfo: [
      {
        name: 'Transaction fee',
        value: 0,
        valueOld: 0,
        scale: 0,
        currency: 'EUR',
      },
    ],
  },
  createdOffer: {
    cardNumber: '',
    amount: 0,
    total: '',
  },
  isOfferLoading: false,
  isConfirmOfferLoading: false,
  isPayInStatusLoading: false,
  isCardsDataLoading: false,
};

const vaultSlice = createSlice({
  name: 'vault',
  initialState: initState,
  reducers: {
    setPhone: (state, action: PayloadAction<string>) => {
      state.phone = action.payload;
    },
    onOfferCreate: (state, action: PayloadAction<SuccessOfferCreateType>) => {
      state.createdOffer = action.payload;
    },
  },
  extraReducers: (builder) => {
    // TODO: type!
    builder
      .addCase(vaultRegistrationRequest.fulfilled, (state, action: PayloadAction<VaultResponse>) => {
        state.phone = action.payload.phone_number;
      })
      .addCase(vaultConnectWalletRequest.fulfilled, (state, action: PayloadAction<VaultResponse>) => {
        state.phone = action.payload.phone_number;
      })
      .addCase(getVaultWallets.fulfilled, (state, action: PayloadAction<VaultWalletResponseType>) => {
        state.wallets = action.payload.wallets;
      })
      .addCase(getVaultTransactionHistory.fulfilled, (state, action: PayloadAction<VaultTransactionHistoryType[]>) => {
        const transactionHistory = action.payload;
        const filteredArray: VaultAllowedTransactionType | [] = [];
        if (transactionHistory.length) {
          transactionHistory.forEach((elem) => {
            const keysArray = Object.keys(elem);
            let transactionModel = null;
            AllowedTransactions.forEach((transaction) => {
              if (keysArray.includes(transaction.model) && elem[transaction.model] !== null) {
                transactionModel = {
                  title: transaction.title,
                  currency: elem[transaction.model].amount?.currency || elem[transaction.model].debitAmount?.currency,
                  value: elem[transaction.model].amount?.value || elem[transaction.model].debitAmount?.value,
                  operationStatus: elem.operationStatus,
                };
              }
            });
            if (transactionModel) {
              filteredArray.push(transactionModel);
            }
          });
        }
        state.transactionHistory = filteredArray;
      })
      .addCase(getVaultProfileData.fulfilled, (state, action: PayloadAction<VaultProfileDataAction>) => {
        state.vaultProfile = action.payload.vaultProfile;
        state.vaultUserInfo = action.payload.vaultUserInfo;
        state.isConfirmUserEmail = !!action.payload.vaultUserInfo?.profile.is_email_verified;
      })
      .addCase(userLogoutRequest.fulfilled, () => initState)
      .addCase(getKYC.fulfilled, (state, action: PayloadAction<VaultKYCResponse>) => {
        state.kyc = action.payload;
      })
      .addCase(createOffer.fulfilled, (state, action: PayloadAction<ResponseOfferType>) => {
        state.offer = action.payload;
      })
      .addMatcher(
        (action) => {
          const { type } = action;
          return type.includes('vault') && type.endsWith('/pending');
        },
        (state) => {
          state.isLoading = true;
        },
      )
      .addMatcher(
        (action) => {
          const { type } = action;
          return type.includes('vault') && type.endsWith('/rejected');
        },
        (state) => {
          state.isLoading = false;
        },
      )
      .addMatcher(
        (action) => {
          const { type } = action;
          return type.includes('vault') && type.endsWith('/fulfilled');
        },
        (state) => {
          state.isLoading = false;
        },
      )
      .addMatcher(
        (action) => {
          const { type } = action;
          return type.includes('vault/kyc') && type.endsWith('/pending');
        },
        (state) => {
          state.isKYCLoading = true;
        },
      )
      .addMatcher(
        (action) => {
          const { type } = action;
          return type.includes('vault/kyc') && type.endsWith('/rejected');
        },
        (state) => {
          state.isKYCLoading = false;
        },
      )
      .addMatcher(
        (action) => {
          const { type } = action;
          return type.includes('vault/kyc') && type.endsWith('/fulfilled');
        },
        (state) => {
          state.isKYCLoading = false;
        },
      )
      .addMatcher(
        (action) => {
          const { type } = action;
          return type.includes('update-offer') && type.endsWith('/pending');
        },
        (state) => {
          state.isOfferLoading = true;
        },
      )
      .addMatcher(
        (action) => {
          const { type } = action;
          return type.includes('update-offer') && type.endsWith('/rejected');
        },
        (state) => {
          state.isOfferLoading = false;
        },
      )
      .addMatcher(
        (action) => {
          const { type } = action;
          return type.includes('update-offer') && type.endsWith('/fulfilled');
        },
        (state) => {
          state.isOfferLoading = false;
        },
      )
      .addMatcher(
        (action) => {
          const { type } = action;
          return type.includes('confirm-offer') && type.endsWith('/pending');
        },
        (state) => {
          state.isConfirmOfferLoading = true;
        },
      )
      .addMatcher(
        (action) => {
          const { type } = action;
          return type.includes('confirm-offer') && type.endsWith('/rejected');
        },
        (state) => {
          state.isConfirmOfferLoading = false;
        },
      )
      .addMatcher(
        (action) => {
          const { type } = action;
          return type.includes('confirm-offer') && type.endsWith('/fulfilled');
        },
        (state) => {
          state.isConfirmOfferLoading = false;
        },
      )
      .addMatcher(
        (action) => {
          const { type } = action;
          return type.includes('payin-status') && type.endsWith('/pending');
        },
        (state) => {
          state.isPayInStatusLoading = true;
        },
      )
      .addMatcher(
        (action) => {
          const { type } = action;
          return type.includes('payin-status') && type.endsWith('/rejected');
        },
        (state) => {
          state.isPayInStatusLoading = false;
        },
      )
      .addMatcher(
        (action) => {
          const { type } = action;
          return type.includes('payin-status') && type.endsWith('/fulfilled');
        },
        (state) => {
          state.isPayInStatusLoading = false;
        },
      )
      .addMatcher(
        (action) => {
          const { type } = action;
          return type.includes('get-cards') && type.endsWith('/pending');
        },
        (state) => {
          state.isCardsDataLoading = true;
        },
      )
      .addMatcher(
        (action) => {
          const { type } = action;
          return type.includes('get-cards') && type.endsWith('/rejected');
        },
        (state) => {
          state.isCardsDataLoading = false;
        },
      )
      .addMatcher(
        (action) => {
          const { type } = action;
          return type.includes('get-cards') && type.endsWith('/fulfilled');
        },
        (state) => {
          state.isCardsDataLoading = false;
        },
      );
  },
});

export const { setPhone, onOfferCreate } = vaultSlice.actions;

export default vaultSlice.reducer;
